import type { PaywallCheckoutPreview } from "@circle-react/types";
import type { PaywallCurrencySmallestUnit } from "@circle-react/types/Paywall";

import { calculateInstallmentPurchaseValue } from "./installmentPurchaseValue";
import { classifyMoneyEventCore } from "./moneyEventClassificationCore";

export interface MoneyEventClassificationInput {
  currencySmallestUnit: PaywallCurrencySmallestUnit;
  hasConfiguredTrial: boolean;
  installmentsCount: number;
  preview?: Pick<
    PaywallCheckoutPreview,
    "amount_due_now" | "preview_scenario" | "trial_period"
  > & {
    paywall_coupon?: Pick<
      NonNullable<PaywallCheckoutPreview["paywall_coupon"]>,
      "amount" | "duration" | "type"
    >;
  };
  priceType: "onetime" | "subscription" | "installments";
  upfrontPaymentAmount: number;
  value: number;
}

export interface MoneyEventClassification {
  eventName: "purchase" | "subscribe" | "start_trial";
  value: number;
}

type EffectivePreview = NonNullable<MoneyEventClassificationInput["preview"]>;

const classifyPaidPreview = (
  input: MoneyEventClassificationInput,
  amountDueNow: number,
  previewScenario: EffectivePreview["preview_scenario"],
): MoneyEventClassification | null => {
  const value =
    input.priceType === "installments"
      ? calculateInstallmentPurchaseValue({
          value: input.value,
          upfrontPaymentAmount: input.upfrontPaymentAmount,
          installmentsCount: input.installmentsCount,
          previewScenario,
          currencySmallestUnit: input.currencySmallestUnit,
          coupon: input.preview?.paywall_coupon,
        })
      : amountDueNow;
  return classifyMoneyEventCore({
    amountDueNow,
    configuredTrial: input.hasConfiguredTrial,
    eventValue: value ?? Number.NaN,
    hasPreview: true,
    priceType: input.priceType,
    previewScenario,
    trialIsFree: false,
  });
};

export const classifyCompletedMoneyEvent = (
  input: MoneyEventClassificationInput,
): MoneyEventClassification | null => {
  const { preview } = input;
  // Without an effective preview, the initial charge is unknown; let the backend report it.
  if (!preview) return null;

  const { amount_due_now: amountDueNow, preview_scenario: scenario } = preview;
  const trialStart = classifyMoneyEventCore({
    amountDueNow,
    configuredTrial: input.hasConfiguredTrial,
    eventValue: 0,
    hasPreview: true,
    priceType: input.priceType,
    previewScenario: scenario,
    trialIsFree: preview.trial_period?.free === true,
  });
  if (!trialStart || trialStart.eventName === "start_trial") return trialStart;
  return classifyPaidPreview(input, amountDueNow, scenario);
};
